import { useState, useCallback, useMemo, useRef } from 'react';

const STORAGE_PREFIX = 'ginastee_table_v1_';

function getColumnKey(col, index) {
  if (col.id) return String(col.id);
  if (col.field) return String(col.field);
  if (typeof col.title === 'string' && col.title) return col.title;
  return `col_${index}`;
}

function getColumnsHash(columns) {
  // Include the original array index so that deliberately reordering columns
  // in source code changes the hash → busts the saved drag order.
  return columns.map((col, i) => `${i}:${getColumnKey(col, i)}`).join('||');
}

/**
 * Manages column ordering with drag-and-drop and localStorage persistence.
 *
 * @param {string|undefined} id        - Unique table key for localStorage
 * @param {Array}            columns   - Original columns array (from props)
 * @param {boolean}          draggable - Whether column dragging is enabled
 */
export default function useTable({ id, columns, draggable }) {
  const storageKey = id ? `${STORAGE_PREFIX}${id}` : null;

  // --- Initial order (try to restore from localStorage) ---
  const getInitialOrder = () => {
    const defaultOrder = columns.map((_, i) => i);
    if (!storageKey || !draggable) return defaultOrder;

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaultOrder;

      const saved = JSON.parse(raw);
      const currentHash = getColumnsHash(columns);

      // Reset if version (columns definition) has changed
      if (
        saved.version !== currentHash ||
        !Array.isArray(saved.order) ||
        saved.order.length !== columns.length ||
        saved.order.some((i) => typeof i !== 'number' || i < 0 || i >= columns.length)
      ) {
        localStorage.removeItem(storageKey);
        return defaultOrder;
      }

      return saved.order;
    } catch (_) {
      return defaultOrder;
    }
  };

  const [columnOrder, setColumnOrder] = useState(getInitialOrder);

  // dragState drives visual feedback; dragRef is read in event handlers
  // to avoid stale closure issues.
  const [dragState, setDragState] = useState({ sourceIndex: null, targetIndex: null });
  const dragRef = useRef({ sourceIndex: null });

  // Ordered columns: map visual indices → original column objects. The drag
  // order is only valid while the columns array is unchanged; when a consumer
  // swaps the column set (e.g. fewer columns on a different tab) the stale order
  // would point past the new array, so we fall back to the natural order instead
  // of mapping to `undefined`.
  const orderedColumns = useMemo(() => {
    const inRange =
      columnOrder.length === columns.length &&
      columnOrder.every((i) => Number.isInteger(i) && i >= 0 && i < columns.length);
    const order = inRange ? columnOrder : columns.map((_, i) => i);
    return order.map((originalIndex) => columns[originalIndex]);
  }, [columnOrder, columns]);

  // --- Persist order to localStorage ---
  const saveOrder = useCallback(
    (order) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ version: getColumnsHash(columns), order })
        );
      } catch (_) {}
    },
    [storageKey, columns]
  );

  // --- Drag handlers (use visual indices within orderedColumns) ---
  const handleDragStart = useCallback((visualIndex) => {
    dragRef.current.sourceIndex = visualIndex;
    setDragState({ sourceIndex: visualIndex, targetIndex: null });
  }, []);

  const handleDragOver = useCallback((visualIndex) => {
    setDragState((prev) =>
      prev.targetIndex === visualIndex ? prev : { ...prev, targetIndex: visualIndex }
    );
  }, []);

  const handleDrop = useCallback(
    (targetVisualIndex) => {
      const sourceVisualIndex = dragRef.current.sourceIndex;
      dragRef.current.sourceIndex = null;
      setDragState({ sourceIndex: null, targetIndex: null });

      if (sourceVisualIndex === null || sourceVisualIndex === targetVisualIndex) return;

      setColumnOrder((order) => {
        const newOrder = [...order];
        const [removed] = newOrder.splice(sourceVisualIndex, 1);
        newOrder.splice(targetVisualIndex, 0, removed);
        saveOrder(newOrder);
        return newOrder;
      });
    },
    [saveOrder]
  );

  const handleDragEnd = useCallback(() => {
    dragRef.current.sourceIndex = null;
    setDragState({ sourceIndex: null, targetIndex: null });
  }, []);

  return {
    orderedColumns,
    dragState,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  };
}
